import { useCallback, useState } from "react";

interface UseSidebarCollapsedOptions {
  /**
   * Arranca colapsado en tablets y pantallas chicas cuando el usuario todavía
   * no ha elegido nada. Una preferencia guardada siempre gana.
   */
  collapseOnSmallScreens?: boolean;
}

/**
 * Debajo de `lg` el menú es un drawer y esto no aplica; el riel arranca en
 * 1024px. Colapsamos por defecto hasta 1279px (tablet en horizontal, laptops
 * chicas) y además en cualquier dispositivo de puntero grueso, para atrapar
 * tablets grandes —una iPad Pro en horizontal mide 1366px y por ancho pasaría
 * por escritorio.
 */
function prefersCollapsedByDefault(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  try {
    return window.matchMedia("(max-width: 1279px), (pointer: coarse)").matches;
  } catch {
    return false;
  }
}

/**
 * Persists the desktop sidebar rail (icon-only) state to localStorage so it
 * survives reloads and navigation within the same module.
 */
export function useSidebarCollapsed(
  storageKey: string,
  { collapseOnSmallScreens = false }: UseSidebarCollapsedOptions = {},
) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    const fallback = collapseOnSmallScreens && prefersCollapsedByDefault();
    try {
      const stored = localStorage.getItem(storageKey);
      // Sólo un valor guardado cuenta como decisión del usuario: si no hay
      // ninguno, manda el tamaño de pantalla.
      if (stored !== null) return stored === "1";
      return fallback;
    } catch {
      return fallback;
    }
  });

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(storageKey, next ? "1" : "0");
      } catch {
        // ignore storage failures (e.g. private mode)
      }
      return next;
    });
  }, [storageKey]);

  return { isCollapsed, toggleCollapsed };
}
